import { useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faCamera } from '@fortawesome/free-solid-svg-icons';

// Phones lead with the camera, so the "before" tile is a photo of a printed
// score rather than the clean engraving the desktop upload flow shows.
import barBeforeMobile from '@Assets/images/home/bar_before_mobile.jpg';
import barAfterMobile from '@Assets/images/home/bar_after_mobile.png';
import { ScoreUploadBox, type ScoreUploadBoxHandle } from '../ScoreUploadBox';
import {
  FinalCtaSection,
  HowItWorksSection,
  SamplesSection,
} from './homeSections';
import { trackButtonClick } from '../../services/telemetry';

/**
 * Phone-first home landing (below `md`). Camera capture is the primary CTA;
 * the existing guest upload pipeline is reused via `ScoreUploadBox`.
 */
export function HomeMobileLanding() {
  const uploadRef = useRef<ScoreUploadBoxHandle>(null);

  const openCamera = (cta: string) => {
    void trackButtonClick('Home', cta);
    uploadRef.current?.openCamera();
  };

  return (
    <div className="mx-auto w-full max-w-[640px] px-1">
      <section className="pt-2 pb-6 text-center">
        <h1
          className={`
            mt-1.5 mb-3 text-[31px] leading-[1.14] font-extrabold tracking-tight
            text-gray-800
            min-[560px]:text-[40px]
          `}
        >
          Snap a photo,
          <br />
          <span className="text-yellow-700">Get every fingering.</span>
        </h1>
        <p className="mx-auto mb-4 max-w-[32ch] text-base text-gray-500">
          Upload any piano score, image or file — AI marks the fingerings.
        </p>

        <div
          className={`
            mb-4 grid grid-cols-[1fr_auto_1fr] items-center gap-2 rounded-[14px]
            border border-gray-200 bg-white p-2.5
          `}
        >
          <div className="rounded-lg bg-[#FFF6E8] p-1 text-center">
            <span
              className={`
                mb-1 block text-[10px] font-bold tracking-wider text-orange-600
                uppercase
              `}
            >
              Bare score
            </span>
            <img
              src={barBeforeMobile}
              alt="Printed piano sheet music, snapped with a phone, before AI fingering"
              className="aspect-square h-auto w-full rounded-md object-cover"
            />
          </div>
          <FontAwesomeIcon
            icon={faArrowRight}
            aria-hidden="true"
            className="text-lg font-extrabold text-yellow-700"
          />
          <div className="rounded-lg bg-[#F1FAF6] p-1 text-center">
            <span
              className={`
                mb-1 block text-[10px] font-bold tracking-wider text-emerald-800
                uppercase
              `}
            >
              Fingered
            </span>
            <img
              src={barAfterMobile}
              alt="The same bar after AI processing, showing suggested fingerings"
              className="aspect-square h-auto w-full rounded-md object-cover"
            />
          </div>
        </div>

        <ScoreUploadBox
          ref={uploadRef}
          variant="guest"
          source="Home"
          layout="camera"
        />
      </section>

      <SamplesSection surface="phone" className="mt-2 mb-1.5" />

      <HowItWorksSection className="mt-10 mb-2.5" />

      <FinalCtaSection
        className="mt-10 mb-2.5"
        label="Take a photo"
        icon={faCamera}
        onClick={() => openCamera('Take a Photo (Final CTA)')}
      />
    </div>
  );
}
